import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Lightbulb } from "lucide-react";
import { get, fmtDate } from "@/lib/api";
import { StatusBadge, ConfBadge } from "@/components/badges";
import { useEventDrawer } from "@/components/EventDrawer";

export default function Signals() {
  const { open } = useEventDrawer();
  const { data } = useQuery({ queryKey: ["events", "weak"], queryFn: () => get("/events", { weak: 1, sort: "interest", order: -1, page_size: 200 }) });
  const { data: habits } = useQuery({ queryKey: ["habits"], queryFn: () => get("/signals/habits") });
  return (
    <div className="p-4 lg:p-6 grid grid-cols-1 [&>*]:min-w-0 xl:grid-cols-[1fr_380px] gap-5">
      <div>
        <div className="flex items-baseline gap-3 mb-1"><h1 className="font-display text-xl font-bold">Signaux faibles</h1><span className="font-mono text-xs text-slate-500">{data?.total ?? "…"}</span></div>
        <p className="text-xs text-slate-500 mb-3">Événements peu médiatisés mais potentiellement significatifs. L'intérêt territorial est distinct de la confiance.</p>
        <div className="space-y-2" data-testid="weak-signals-list">
          {(data?.items || []).map((e) => (
            <button key={e.id} onClick={() => open(e.id)} data-testid={`weak-${e.id}`} className="panel w-full text-left p-3 border-l-4 border-l-indigo-600 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors duration-150 flex gap-4">
              <div className="w-16 shrink-0 text-center">
                <div className="overline !text-[9px]">Intérêt</div>
                <div className="font-mono text-2xl font-semibold text-indigo-700 dark:text-indigo-400">{e.interest}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5"><StatusBadge status={e.status} /><span className="font-mono text-[11px] text-slate-500">{fmtDate(e.next_date)} {e.start_time || "heure inconnue"}</span></div>
                <div className="font-medium text-sm mt-0.5">{e.title}</div>
                <div className="text-[11px] text-slate-500">{e.commune_name} · {e.venue_name || "lieu inconnu"} · {e.organizer_name || "organisateur inconnu"}</div>
              </div>
              <div className="shrink-0 text-right"><div className="overline !text-[9px]">Confiance</div><ConfBadge value={e.confidence} /></div>
            </button>
          ))}
          {data?.items?.length === 0 && <div className="panel p-8 text-center text-xs text-slate-400">Aucun signal faible détecté pour l'instant.</div>}
        </div>
      </div>
      <aside>
        <h2 className="font-display text-base font-bold flex items-center gap-1.5 mb-1"><Lightbulb className="h-4 w-4" />Événements habituels non encore détectés</h2>
        <p className="text-[11px] text-slate-500 mb-3">Suggestions de veille issues de l'apprentissage des habitudes. <b>Ce ne sont pas des événements réels.</b></p>
        <div className="space-y-2" data-testid="habits-list">
          {(habits || []).map((h, i) => (
            <div key={i} className="panel p-3 border-dashed border-amber-400 bg-amber-50/40 dark:bg-amber-950/20">
              <div className="font-mono text-[9px] uppercase tracking-wider text-amber-800 dark:text-amber-300 mb-1">Suggestion de veille · {h.periodicity}</div>
              <div className="text-xs">{h.message}</div>
              <Link to={`/organisateurs?id=${h.organizer_id}`} className="text-[11px] text-blue-700 hover:underline">{h.organizer_name} · {h.occurrences} occurrences passées</Link>
            </div>
          ))}
          {habits?.length === 0 && <div className="panel p-4 text-xs text-slate-400">L'historique n'est pas encore suffisant pour identifier des habitudes (au moins 2 éditions régulières d'un même organisateur).</div>}
        </div>
      </aside>
    </div>
  );
}
